import React, { useMemo, useState } from 'react';
import { deleteGuideMedia, reorderGallery, updateGuideMedia } from '../../../../api/stays-guide-client';
import { StaysApiError } from '../../../../api/stays-client';
import type { GuideMedia } from '../../../../interfaces/stays-guide';
import { AppButton } from '../../../button/app-button';
import { Field, FieldError, Input, Label } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { ToggleChip } from '../../../toggle-chip/toggle-chip';
import { ProgressFill, ProgressTrack } from '../../../gallery/entity-gallery.styled';
import { DisabledReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { ERROR_COPY, refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import toast from '../../../../utils/toast';
import { Counter, FileButton, HiddenFile, Muted, Section, SectionHead, Thumb } from './guide-editor.styled';
import { TileCard, TileControls, TileFrame, TileGrid, TileMeta, TileTitle } from './gallery-manager.styled';
import { galleryOf, moved, photoName } from './guide-model';
import { useGuideShare } from './guide-share';
import { ACCEPT_PHOTOS, MediaUpdate, uploadCompressed, uploadErrorText } from './guide-upload';

/**
 * THE GALLERY TAB (plan S7.8): up to 40 photos, in the host's order. "Add
 * photos" takes several at once and sends them ONE AT A TIME, each compressed
 * first. Per photo: its title and category (a FormModal), "Show only after
 * check-in is complete" (aria-pressed), Share, Delete (asks first), Move
 * earlier / later. Share fetches the bytes ahead, on the tile's focus or
 * pointer (guide-share.ts). Share needs only stays:read; everything else
 * stays:manage.
 */

export interface GalleryManagerProps {
  listingId: string;
  media: GuideMedia[];
  limit: number;
  canManage: boolean;
  onMediaChange: MediaUpdate;
  onReload: () => void | Promise<void>;
}

const withGallery = (gallery: GuideMedia[]) => (cur: GuideMedia[]) => [...cur.filter(m => m.slot !== 'gallery'), ...gallery];

export const GalleryManager: React.FC<GalleryManagerProps> = ({ listingId, media, limit, canManage, onMediaChange, onReload }) => {
  const gallery = useMemo(() => galleryOf(media), [media]);
  const [query, setQuery] = useState('');
  const [upload, setUpload] = useState<{ index: number; total: number; percent: number } | null>(null);
  const [editing, setEditing] = useState<GuideMedia | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<GuideMedia | null>(null);
  const { prefetch, share } = useGuideShare(listingId);

  const q = query.trim().toLowerCase();
  const shown = q
    ? gallery.filter(m => [m.title || '', m.category || ''].some(s => s.toLowerCase().includes(q)))
    : gallery;
  const full = gallery.length >= limit;

  const addPhotos = async (files: File[]) => {
    if (!files.length || upload) return;
    const room = Math.max(0, limit - gallery.length);
    const batch = files.slice(0, room);
    if (files.length > room) toast.error(ERROR_COPY.GALLERY_LIMIT);
    let added = 0;
    for (let i = 0; i < batch.length; i += 1) {
      setUpload({ index: i + 1, total: batch.length, percent: 0 });
      try {
        const m = await uploadCompressed(listingId, batch[i], { slot: 'gallery' }, percent =>
          setUpload({ index: i + 1, total: batch.length, percent })
        );
        added += 1;
        onMediaChange(cur => [...cur, m]);
      } catch (err) {
        toast.error(uploadErrorText(err));
        if ((err as StaysApiError) && (err as StaysApiError).code === 'GALLERY_LIMIT') break;
      }
    }
    setUpload(null);
    if (added) toast.success(added === 1 ? 'Photo added' : `${added} photos added`);
  };

  const run = async (key: string, fn: () => Promise<void>, fallback: string) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      const e = err as StaysApiError;
      toast.error(refusalText(e, fallback));
      if (e && e.code === 'LIST_CHANGED') void onReload();
    } finally {
      setBusy(null);
    }
  };

  const toggle = (m: GuideMedia, next: boolean) =>
    run(`toggle-${m.id}`, async () => {
      const out = await updateGuideMedia(listingId, m.id, { sensitive: next });
      onMediaChange(cur => cur.map(x => (x.id === m.id ? out.media : x)));
    }, 'Could not save the photo. Try again.');

  const move = (index: number, delta: number) => {
    const next = moved(gallery, index, delta);
    if (next === gallery) return undefined;

    return run(`move-${gallery[index].id}`, async () => {
      const out = await reorderGallery(listingId, next.map(m => m.id));
      onMediaChange(withGallery(out.media));
    }, 'Could not move the photo. Try again.');
  };

  const openEdit = (m: GuideMedia) => {
    setTitle(m.title || '');
    setCategory(m.category || '');
    setErrors({});
    setEditing(m);
  };

  const saveEdit = async () => {
    if (!editing || busy) return;
    setBusy('edit');
    setErrors({});
    try {
      const out = await updateGuideMedia(listingId, editing.id, { title: title.trim() || null, category: category.trim() || null });
      onMediaChange(cur => cur.map(x => (x.id === out.media.id ? out.media : x)));
      setEditing(null);
      toast.success('Photo details saved');
    } catch (err) {
      const e = err as StaysApiError;
      if (e && e.status === 400 && Object.keys(e.fields || {}).length) setErrors(e.fields);
      else toast.error(refusalText(e, 'Could not save the photo. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (m: GuideMedia) => {
    try {
      await deleteGuideMedia(listingId, m.id);
      onMediaChange(cur => cur.filter(x => x.id !== m.id));
      toast.success('Photo deleted');
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Could not delete the photo. Try again.'));
    }
  };

  const doShare = async (m: GuideMedia, name: string) => {
    try {
      const outcome = await share(m, name);
      if (outcome === 'downloaded') toast.success('Photo saved to your device');
    } catch (err) {
      toast.error(refusalText(err as StaysApiError, 'Could not get the photo. Try again.'));
    }
  };

  const editDirty = !!editing && (title !== (editing.title || '') || category !== (editing.category || ''));

  return (
    <Section aria-label="Gallery">
      <SectionHead>
        <Counter data-testid="gallery-count">{`${gallery.length} of ${limit}`}</Counter>
        {canManage ? (
          <div>
            <FileButton htmlFor="guide-gallery-add" $disabled={full || !!upload}>
              Add photos
              <HiddenFile
                id="guide-gallery-add"
                type="file"
                multiple
                accept={ACCEPT_PHOTOS}
                disabled={full || !!upload}
                aria-describedby={full ? 'guide-gallery-full' : undefined}
                onChange={e => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  e.target.value = '';
                  void addPhotos(files);
                }}
              />
            </FileButton>
            {full ? <DisabledReason id="guide-gallery-full">{ERROR_COPY.GALLERY_LIMIT}</DisabledReason> : null}
          </div>
        ) : null}
      </SectionHead>

      {upload ? (
        <div>
          <ProgressTrack
            role="progressbar"
            aria-label={`Uploading photo ${upload.index} of ${upload.total}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={upload.percent}
          >
            <ProgressFill $percent={upload.percent} />
          </ProgressTrack>
          <Muted>{`Uploading photo ${upload.index} of ${upload.total}…`}</Muted>
        </div>
      ) : null}

      {gallery.length ? (
        <Field>
          <Label htmlFor="gallery-search">Search photos</Label>
          <Input id="gallery-search" type="search" value={query} onChange={e => setQuery(e.target.value)} />
        </Field>
      ) : null}

      {!gallery.length ? (
        <Muted role="status">No gallery photos yet.</Muted>
      ) : !shown.length ? (
        <Muted role="status">No photos match your search.</Muted>
      ) : (
        <TileGrid aria-label="Gallery photos">
          {shown.map(m => {
            const index = gallery.findIndex(x => x.id === m.id);
            const name = photoName(m, index);
            const titleId = `gallery-title-${m.id}`;

            return (
              <TileCard
                key={m.id}
                data-media={m.id}
                onFocus={() => prefetch(m.id)}
                onPointerEnter={() => prefetch(m.id)}
              >
                <TileFrame>
                  <Thumb src={m.url} alt={m.title || `Photo ${index + 1}`} loading="lazy" />
                </TileFrame>
                <TileTitle id={titleId}>{m.title || `Photo ${index + 1}`}</TileTitle>
                {m.category ? <TileMeta>{m.category}</TileMeta> : null}
                {canManage ? (
                  <ToggleChip
                    pressed={m.sensitive}
                    disabled={busy === `toggle-${m.id}`}
                    aria-describedby={titleId}
                    onToggle={next => void toggle(m, next)}
                  >
                    Show only after check-in is complete
                  </ToggleChip>
                ) : m.sensitive ? (
                  <TileMeta>Shown only after check-in is complete</TileMeta>
                ) : null}
                <TileControls>
                  <AppButton variant="ghost" size="compact" aria-label={`Share ${name}`} onClick={() => void doShare(m, name)}>
                    Share
                  </AppButton>
                  {canManage ? (
                    <>
                      <AppButton variant="ghost" size="compact" aria-label={`Edit details of ${name}`} onClick={() => openEdit(m)}>
                        Edit details
                      </AppButton>
                      <AppButton variant="ghost" size="compact" aria-label={`Delete ${name}`} onClick={() => setDeleting(m)}>
                        Delete
                      </AppButton>
                      {!q ? (
                        <>
                          <AppButton variant="ghost" size="compact" aria-describedby={titleId}
                            disabled={index <= 0 || !!busy} onClick={() => void move(index, -1)}>
                            Move earlier
                          </AppButton>
                          <AppButton variant="ghost" size="compact" aria-describedby={titleId}
                            disabled={index >= gallery.length - 1 || !!busy} onClick={() => void move(index, 1)}>
                            Move later
                          </AppButton>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </TileControls>
              </TileCard>
            );
          })}
        </TileGrid>
      )}

      <FormModal
        open={!!editing}
        title="Photo details"
        onClose={() => setEditing(null)}
        isDirty={editDirty}
        saving={busy === 'edit'}
        footer={
          <>
            <AppButton variant="ghost" disabled={busy === 'edit'} onClick={() => setEditing(null)}>
              Cancel
            </AppButton>
            <AppButton variant="primary" loading={busy === 'edit'} loadingLabel="Saving…" onClick={() => void saveEdit()}>
              Save details
            </AppButton>
          </>
        }
      >
        <form
          id="gallery-edit-form"
          noValidate
          onSubmit={e => {
            e.preventDefault();
            void saveEdit();
          }}
        >
          <Field>
            <Label htmlFor="gallery-edit-title">Title</Label>
            <Input id="gallery-edit-title" value={title} maxLength={120} hasError={!!errors.title}
              onChange={e => setTitle(e.target.value)} />
            <FieldError message={errors.title} />
          </Field>
          <Field>
            <Label htmlFor="gallery-edit-category">Category</Label>
            <Input id="gallery-edit-category" value={category} maxLength={40} hasError={!!errors.category}
              onChange={e => setCategory(e.target.value)} />
            <FieldError message={errors.category} />
          </Field>
        </form>
      </FormModal>

      <ConfirmDialog
        open={!!deleting}
        title={deleting ? `Delete ${photoName(deleting, gallery.findIndex(x => x.id === deleting.id))}?` : 'Delete photo?'}
        message="Guests stop seeing this photo in the guide at once."
        primaryLabel="Delete photo"
        primaryVariant="destructive"
        dismissLabel="Keep photo"
        onPrimary={async () => {
          if (deleting) await remove(deleting);
          setDeleting(null);
        }}
        onDismiss={() => setDeleting(null)}
      />
    </Section>
  );
};
